/**
 * 매장 소식 — 추가 · 수정 모달 (/my/news/ · page-partner-news.php).
 *
 * 틀(분류 · 기한 · 타이틀)은 PHP 가 그리고, 대표 사진과 설명글은 설정 화면과 같은
 * 공용 컴포넌트(imagePicker · richText)를 비워 둔 자리에 끼운다. 여닫기는 initModal.js 가 맡는다.
 *
 * 모달은 하나다. [+ 매장 소식 추가]로 열면 빈 추가 모드, 카드를 누르면 그 카드의
 * data-news-edit 값으로 채운 수정 모드가 된다. 같은 대상을 다시 열면 고치던 내용이 그대로 있고,
 * 다른 대상으로 바꿔 열 때만 비우고 새로 채운다.
 *
 * 저장 · 삭제는 admin-ajax(Partner_News)를 거쳐 매장 사이트 qrshop-api 로 간다.
 * 그동안 모달을 잠그고(lockModal — 누른 버튼 "저장 중…" · 모든 버튼 비활성 · 닫기 막음),
 * 끝나면 모달을 닫고 목록을 서버가 다시 그리도록 새로고침한다.
 * 알림은 새로고침 뒤에 띄운다(sessionStorage 에 잠깐 맡긴다). 실패하면 잠금을 풀고 footer 에 알린다.
 * 보내기 · 안내 · 새로고침은 매장 케어와 같은 조각(partnerForm.js)을 쓴다.
 */

import { createImagePicker } from './imagePicker';
import { lockModal } from './initModal';
import { readOrder } from './initOrderList';
import { postForm, reloadWith as reloadWithKey, setNotice, showFlash } from './partnerForm';
import { createModeEditor } from './modeEditor';
import createAuthorField from './partnerAuthor';

/** 새로고침한 화면에 띄울 알림을 맡겨 두는 자리 */
const FLASH_KEY = 'qrshop_news_flash';

/** 알림을 맡기고 목록을 다시 그리게 한다. url 을 주면 그 주소로 옮긴다. */
const reloadWith = (message, url = '') => reloadWithKey(FLASH_KEY, message, url);

const postNews = postForm;

const InitPartnerNews = () => {
  const form = document.querySelector('[data-news-form]');

  if (!form) {
    return;
  }

  showFlash(FLASH_KEY);

  // 폼은 모달 body 이고, 저장·삭제 버튼과 결과 안내는 footer 에 있다 (저장 버튼은 form 속성으로 이어진다)
  const dialog = form.closest('dialog');
  const heading = dialog?.querySelector('.modal__title');
  const status = dialog?.querySelector('[data-news-status]');
  const submit = dialog?.querySelector(`[type="submit"][form="${form.getAttribute('id')}"]`);
  const deleteButton = dialog?.querySelector('[data-news-delete]');
  const locked = form.querySelector('[data-news-locked]');
  const contentFields = form.querySelectorAll('[data-news-content]');
  const idInput = form.elements.namedItem('post_id');
  const titleInput = form.elements.namedItem('title');
  const categorySelect = form.elements.namedItem('category');
  const expiresInput = form.elements.namedItem('expires');
  const stickyInput = form.elements.namedItem('sticky');
  const sharedReadonly = form.querySelector('[data-news-shared-readonly]');
  const author = createAuthorField(form);

  // 사진은 고르는 즉시 webp data URL 로 바뀌어 여기 담긴다 (되돌리면 빈 값 = 원래 사진 유지)
  let imageDataUrl = '';

  // 지금 모달에 들어 있는 대상 — 'add' 또는 'edit-{id}'
  let target = 'add';

  const setStatus = (message, state = 'error') => setNotice(status, message, state);

  const picker = createImagePicker({
    initialUrl: '',
    side: true,
    onStaged: (dataUrl) => {
      imageDataUrl = dataUrl || '';
    },
    onError: (message) => setStatus(message),
  });

  const body = createModeEditor({ placeholder: '설명글을 입력해 주세요.' });

  form.querySelector('[data-news-image]')?.append(picker.element);
  form.querySelector('[data-news-body]')?.append(body.element);

  // 라벨 줄 오른쪽 — Simple / Pro 전환 버튼
  form.querySelector('[data-news-body-mode]')?.replaceWith(body.switcher);

  // 고치기 시작하면 지난번 안내는 걷어 낸다
  form.addEventListener('input', () => setStatus(''));

  /** 원장이 고치면 깨지는 글(관리자가 넣은 영상 등)은 사진·설명글 칸을 감추고 안내를 띄운다 */
  const setEditable = (editable) => {
    contentFields.forEach((field) => {
      field.hidden = !editable;
    });

    if (locked) {
      locked.hidden = editable;
    }
  };

  /**
   * 통합 계정이 쓴 공통 소식을 원장(다른 계정)이 열었다 — 보기만. 저장 · 삭제 버튼을 잠그고 안내를 띄운다.
   * 판단은 매장 사이트가 한 번 더 한다(고칠 수 없는 글이면 거절한다).
   */
  const setReadonly = (readonly) => {
    if (sharedReadonly) {
      sharedReadonly.hidden = !readonly;
    }

    if (submit) {
      submit.disabled = readonly;
    }
  };

  /** 모든 칸을 처음 상태로 — 분류는 PHP 가 골라 둔 기본값(공지사항)으로 돌아간다 */
  const clear = (imageUrl = '') => {
    form.reset();
    author.reset();
    setReadonly(false);

    // hidden 입력은 value 가 곧 기본값이라 reset() 으로 비워지지 않는다.
    // 남겨 두면 [추가]가 방금 고치던 글을 덮어쓴다 — 직접 비운다.
    idInput.value = '';
    picker.setInitial(imageUrl);
    body.setContent('');
    setStatus('');
    setEditable(true);

    // reset() 은 input 이벤트를 내지 않아 기한 칸의 안내 글자가 되살아나지 않는다
    expiresInput?.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const openAdd = () => {
    if ('add' === target) {
      return;
    }

    target = 'add';
    clear();

    if (heading) {
      heading.textContent = '매장 소식 추가';
    }

    if (deleteButton) {
      deleteButton.hidden = true;
    }
  };

  /** @param {{ id: number, title: string, category: number, expires: string, sticky: boolean, image: string, body: string, mode: string, locked: boolean, shared: boolean, can_edit: boolean }} news */
  const openEdit = (news) => {
    const next = `edit-${news.id}`;

    if (next === target) {
      return;
    }

    target = next;
    clear(news.image || '');

    idInput.value = String(news.id);
    titleInput.value = news.title || '';
    stickyInput.checked = true === news.sticky;
    expiresInput.value = news.expires || '';
    expiresInput.dispatchEvent(new Event('input', { bubbles: true }));

    // 목록에 없는 분류(미분류 등)면 기본값(공지사항)이 남는다 — 저장하면 그 분류로 바뀐다
    if (
      categorySelect &&
      [...categorySelect.options].some((option) => option.value === String(news.category))
    ) {
      categorySelect.value = String(news.category);
    }

    body.setContent(news.body || '', news.mode);
    setEditable(true !== news.locked);
    author.lock(true === news.shared);

    const canEdit = false !== news.can_edit;
    setReadonly(!canEdit);

    if (heading) {
      heading.textContent = canEdit ? '매장 소식 수정' : '공통 소식';
    }

    if (deleteButton) {
      deleteButton.hidden = !canEdit;
    }
  };

  // 여는 일은 initModal.js 가 한다. 여기서는 무엇으로 열었는지 보고 내용만 채운다.
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-partner-add]')) {
      openAdd();
      return;
    }

    const card = event.target.closest?.('[data-news-edit]');

    if (!card) {
      return;
    }

    try {
      openEdit(JSON.parse(card.dataset.newsEdit || '{}'));
    } catch (error) {
      setStatus('소식을 불러오지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.');
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const title = titleInput instanceof HTMLInputElement ? titleInput.value.trim() : '';

    if ('' === title) {
      setStatus('타이틀을 입력해 주세요.');
      titleInput?.focus();
      return;
    }

    const data = new FormData(form);
    // 추가 모드면 어떤 경우에도 새 글이다 (남의 글을 덮어쓰지 않도록 한 번 더 막는다)
    const id = 'add' === target ? 0 : Number(data.get('post_id') || 0);
    const category = Number(data.get('category') || 0);

    // 매장 사이트에서 분류를 못 받아 오면 선택 칸이 잠겨 값이 비어 있다
    if (!category) {
      setStatus('분류를 불러오지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.');
      return;
    }

    // 사진을 올리느라 몇 초 걸릴 수 있다 — 그동안 모달을 잠근다
    const unlock = lockModal(dialog, submit, '저장 중…');

    try {
      const result = await postNews(form, form.dataset.action || '', {
        id,
        category,
        expires: String(data.get('expires') || ''),
        // 체크 안 한 체크박스는 FormData 에 없으므로 직접 0/1 로 보낸다 (고칠 때 해제도 전해야 한다)
        sticky: stickyInput.checked ? 1 : 0,
        title,
        image: imageDataUrl,
        body: body.getHTML(),
        mode: body.getMode(),
        // 새 글에서만 뜻이 있다 — 고칠 때는 매장 사이트가 원래 작성자를 그대로 둔다
        author: id ? 'shop' : author.value(),
      });

      // 끝났으면 먼저 닫는다. 새 소식은 진행 중 목록에 들어가므로 그 목록으로 옮기고,
      // 고친 것은 보던 목록 그대로 다시 그린다.
      dialog.close();
      unlock();
      reloadWith(result.message || '저장했습니다.', id ? '' : form.dataset.listUrl || '');
    } catch (error) {
      unlock();
      setStatus(error.message);
    }
  });

  deleteButton?.addEventListener('click', async () => {
    const id = Number(idInput.value || 0);

    if (!id) {
      return;
    }

    // 매장 사이트에서는 휴지통으로 가지만 원장은 되살릴 방법이 없으므로 한 번 묻는다
    const sharedPost = 'shared' === author.value();

    if (
      !window.confirm(
        sharedPost
          ? '이 공통 소식을 삭제할까요?\n관리하는 모든 매장 페이지에서 바로 사라집니다.'
          : '이 소식을 삭제할까요?\n매장 페이지에서도 바로 사라집니다.'
      )
    ) {
      return;
    }

    const unlock = lockModal(dialog, deleteButton, '삭제 중…');

    try {
      const result = await postNews(form, form.dataset.deleteAction || '', { id });

      dialog.close();
      unlock();
      reloadWith(result.message || '삭제했습니다.');
    } catch (error) {
      unlock();
      setStatus(error.message);
    }
  });

  // ── 순서 변경 모달 — 진행 중 소식이 둘 이상일 때만 있다 ──────────────
  const orderDialog = document.getElementById('news-order-modal');
  const orderList = orderDialog?.querySelector('[data-order-list]');
  const orderSave = orderDialog?.querySelector('[data-order-save]');
  const orderStatus = orderDialog?.querySelector('[data-order-status]');

  orderSave?.addEventListener('click', async () => {
    setNotice(orderStatus, '');

    const unlock = lockModal(orderDialog, orderSave, '저장 중…');

    try {
      const result = await postNews(form, form.dataset.orderAction || '', {
        ids: readOrder(orderList).join(','),
      });

      orderDialog.close();
      unlock();
      reloadWith(result.message || '순서를 바꿨습니다.');
    } catch (error) {
      unlock();
      setNotice(orderStatus, error.message);
    }
  });
};

export default InitPartnerNews;
